import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { api, BACKEND_URL, CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Check, FolderHeart, Film, AlertCircle, Trash2, FileVideo } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024 * 1024;  // 5 GB — S3 single-PUT ceiling
const ACCEPTED = ["video/mp4", "video/quicktime", "video/x-matroska", "video/webm"];

/**
 * Reusable uploader for already-cut clips.
 * Single direct-to-S3 PUT per file (no multipart) since clips are ≤ 5 GB.
 *
 * Props:
 *  - galleryId?      pre-select a gallery and lock the picker
 *  - galleryName?    label shown when picker is locked
 *  - association?    pre-lock the association (e.g. from a performance page)
 *  - performanceName?pre-lock the performance / event name
 *  - onClose()       close handler
 *  - onUploaded()    called once at least 1 clip finished — parent should refresh
 */
export default function ClipUploader({ galleryId = "", galleryName = "", association = "", performanceName = "", embedded = false, onClose, onUploaded }) {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]); // { id, file, name, status, progress, error, sub_label, rider_horse_name }
  const [category, setCategory] = useState(CATEGORIES[0]?.id || "barrel");
  const [eventName, setEventName] = useState(performanceName || "");
  const [eventDate, setEventDate] = useState("");
  const [galleries, setGalleries] = useState([]);
  const [galleryIds, setGalleryIds] = useState(new Set(galleryId ? [galleryId] : []));
  const [drag, setDrag] = useState(false);
  const [running, setRunning] = useState(false);
  const lockedGallery = Boolean(galleryId);
  const lockedPerformance = Boolean(performanceName);

  useEffect(() => {
    if (lockedGallery) return;
    api.get("/galleries").then((r) => setGalleries(r.data.galleries || [])).catch(() => {});
  }, [lockedGallery]);

  const addFiles = (files) => {
    const accepted = [];
    for (const f of files) {
      const okType = ACCEPTED.includes(f.type) || /\.(mp4|mov|mkv|webm)$/i.test(f.name);
      if (!okType) {
        toast.error(`${f.name} is not a supported video file`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over 5 GB`);
        continue;
      }
      accepted.push({
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
        size: f.size,
        status: "pending", // pending | uploading | finalizing | done | failed
        progress: 0,
        error: "",
        sub_label: f.name.replace(/\.[^.]+$/, "").slice(0, 80),
        rider_horse_name: "",
      });
    }
    setRows((r) => [...r, ...accepted]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer?.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  const remove = (id) => setRows((rs) => rs.filter((r) => r.id !== id));
  const patchRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const toggleGallery = (id) => {
    setGalleryIds((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const uploadOne = async (row) => {
    try {
      patchRow(row.id, { status: "uploading", progress: 0, error: "" });
      const presign = await api.post("/clips/import/presign", {
        filename: row.file.name,
        file_size: row.file.size,
        content_type: row.file.type || "video/mp4",
      });
      const { clip_id, upload_url } = presign.data;

      // Direct browser → S3 PUT
      await axios.put(upload_url, row.file, {
        headers: { "Content-Type": row.file.type || "video/mp4" },
        onUploadProgress: (e) => {
          if (e.total) patchRow(row.id, { progress: Math.round((e.loaded / e.total) * 100) });
        },
      });

      patchRow(row.id, { status: "finalizing", progress: 100 });
      await api.post("/clips/import/finalize", {
        clip_id,
        filename: row.file.name,
        category,
        sub_label: row.sub_label,
        rider_horse_name: row.rider_horse_name,
        event_name: eventName,
        event_date: eventDate,
        association,
        performance_name: performanceName || eventName,
        gallery_ids: Array.from(galleryIds),
      });
      patchRow(row.id, { status: "done" });
      return true;
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Upload failed";
      patchRow(row.id, { status: "failed", error: String(msg).slice(0, 200) });
      return false;
    }
  };

  const startAll = async () => {
    if (running || rows.length === 0) return;
    setRunning(true);
    let okCount = 0;
    // Upload sequentially (most rodeo Wi-Fi can't handle 5 parallel PUTs)
    for (const r of rows) {
      if (r.status === "done") { okCount++; continue; }
      const ok = await uploadOne(r);
      if (ok) okCount++;
    }
    setRunning(false);
    if (okCount > 0) {
      toast.success(`${okCount} clip${okCount === 1 ? "" : "s"} uploaded`);
      onUploaded && onUploaded(okCount);
    }
  };

  const allDone = rows.length > 0 && rows.every((r) => r.status === "done");

  // Embedded rendering: no outer overlay, no header — the parent modal owns
  // both. Just render the body so the uploader can be tabbed into another
  // dialog (e.g. Add Clips to Performance → "Upload New Videos" tab).
  if (embedded) {
    return (
      <div data-testid="clip-uploader-embedded" className="w-full">
        {lockedPerformance && (
          <div className="px-6 pt-4 text-xs text-orange-400 inline-flex items-center gap-1.5">
            <Film size={11} /> Will be tagged with performance: <span className="font-semibold">{performanceName}</span>
            {association && <span className="text-stone-500">· under {association}</span>}
          </div>
        )}
        <UploaderBody
          category={category} setCategory={setCategory}
          eventName={eventName} setEventName={setEventName}
          eventDate={eventDate} setEventDate={setEventDate}
          galleries={galleries} galleryIds={galleryIds} toggleGallery={toggleGallery}
          lockedGallery={lockedGallery} lockedPerformance={lockedPerformance}
          rows={rows} remove={remove} patchRow={patchRow}
          drag={drag} setDrag={setDrag} onDrop={onDrop} fileInputRef={fileInputRef} addFiles={addFiles}
          running={running} allDone={allDone} startAll={startAll} onClose={onClose}
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-4xl" onClick={(e) => e.stopPropagation()} data-testid="clip-uploader">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
          <div>
            <div className="label-tiny mb-1">Import Clips</div>
            <h2 className="font-display uppercase text-2xl font-bold">Upload Already-Cut Clips</h2>
            {lockedGallery && galleryName && (
              <div className="text-xs text-orange-400 mt-1 inline-flex items-center gap-1.5">
                <FolderHeart size={11} /> Will be added to: <span className="font-semibold">{galleryName}</span>
              </div>
            )}
            {lockedPerformance && (
              <div className="text-xs text-orange-400 mt-1 inline-flex items-center gap-1.5">
                <Film size={11} /> Will be tagged with performance: <span className="font-semibold">{performanceName}</span>
                {association && <span className="text-stone-500">· under {association}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200" data-testid="uploader-close"><X size={20} /></button>
        </div>

        <UploaderBody
          category={category} setCategory={setCategory}
          eventName={eventName} setEventName={setEventName}
          eventDate={eventDate} setEventDate={setEventDate}
          galleries={galleries} galleryIds={galleryIds} toggleGallery={toggleGallery}
          lockedGallery={lockedGallery} lockedPerformance={lockedPerformance}
          rows={rows} remove={remove} patchRow={patchRow}
          drag={drag} setDrag={setDrag} onDrop={onDrop} fileInputRef={fileInputRef} addFiles={addFiles}
          running={running} allDone={allDone} startAll={startAll} onClose={onClose}
        />
      </div>
    </div>
  );
}


/** Split-out body so both the standalone modal and the "embedded" mode
 *  share exactly the same form + drop zone + progress list. */
function UploaderBody({
  category, setCategory, eventName, setEventName, eventDate, setEventDate,
  galleries, galleryIds, toggleGallery, lockedGallery, lockedPerformance,
  rows, remove, patchRow, drag, setDrag, onDrop, fileInputRef, addFiles,
  running, allDone, startAll, onClose,
}) {
  return (
    <>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Category" required>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="field-input w-full" data-testid="up-category">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Event name">
              <input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Royal Crown Day 1"
                className="field-input w-full"
                data-testid="up-event"
                disabled={lockedPerformance}
                readOnly={lockedPerformance}
              />
            </Field>
            <Field label="Event date">
              <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="field-input w-full" data-testid="up-event-date" />
            </Field>
          </div>

          {!lockedGallery && (
            <Field label="Add to galleries (optional)">
              {galleries.length === 0 ? (
                <p className="text-xs text-stone-500">No galleries yet — clips will land in the Library only.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border border-stone-800 p-2" data-testid="up-galleries">
                  {galleries.map((g) => {
                    const on = galleryIds.has(g.id);
                    return (
                      <button
                        type="button"
                        key={g.id}
                        onClick={() => toggleGallery(g.id)}
                        className={`text-left p-2 border text-xs flex items-center gap-2 transition-colors ${on ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
                        data-testid={`up-gallery-${g.id}`}
                      >
                        <span className={`w-3.5 h-3.5 border ${on ? "border-orange-500 bg-orange-600/30" : "border-stone-600"} inline-flex items-center justify-center shrink-0`}>
                          {on && <Check size={9} className="text-orange-400" />}
                        </span>
                        <span className="truncate">{g.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Field>
          )}

          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? "border-orange-500 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
            data-testid="up-dropzone"
          >
            <Upload size={28} className="mx-auto text-stone-500 mb-2" />
            <div className="text-stone-300 text-sm">Drop video files here or <span className="text-orange-400 underline">browse</span></div>
            <div className="text-stone-500 text-xs mt-1">MP4 / MOV / MKV / WebM · up to 5 GB each</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="video/*"
              className="hidden"
              onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
              data-testid="up-file-input"
            />
          </div>

          {rows.length > 0 && (
            <div className="border border-stone-800" data-testid="up-rows">
              <div className="grid grid-cols-[1fr_140px_140px_120px_40px] gap-2 px-3 py-2 border-b border-stone-800 text-[10px] uppercase tracking-widest text-stone-500">
                <div>File</div><div>Rider / Horse</div><div>Label</div><div>Status</div><div></div>
              </div>
              {rows.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_140px_140px_120px_40px] gap-2 px-3 py-2 border-b border-stone-900 items-center" data-testid={`up-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm truncate inline-flex items-center gap-1.5">
                      <FileVideo size={12} className="text-stone-500 shrink-0" />
                      <span className="truncate">{r.name}</span>
                    </div>
                    <div className="text-[10px] text-stone-500 font-mono-tc">{(r.size / 1024 / 1024).toFixed(1)} MB</div>
                  </div>
                  <input
                    value={r.rider_horse_name}
                    onChange={(e) => patchRow(r.id, { rider_horse_name: e.target.value })}
                    placeholder="Sarah / Tex"
                    className="field-input !py-1 text-xs"
                    disabled={r.status !== "pending"}
                    data-testid={`up-rider-${r.id}`}
                  />
                  <input
                    value={r.sub_label}
                    onChange={(e) => patchRow(r.id, { sub_label: e.target.value })}
                    placeholder="Label"
                    className="field-input !py-1 text-xs"
                    disabled={r.status !== "pending"}
                    data-testid={`up-label-${r.id}`}
                  />
                  <StatusCell row={r} />
                  <button
                    onClick={() => remove(r.id)}
                    disabled={r.status === "uploading" || r.status === "finalizing"}
                    className="text-stone-500 hover:text-rose-400 disabled:opacity-40"
                    title="Remove"
                    data-testid={`up-remove-${r.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-stone-800">
            <button onClick={onClose} className="btn-secondary flex-1" data-testid="up-cancel">
              {allDone ? "Done" : "Cancel"}
            </button>
            <button
              onClick={startAll}
              disabled={running || rows.length === 0 || allDone}
              className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
              data-testid="up-start"
            >
              <Upload size={13} />
              {running ? "Uploading…" : allDone ? "All Uploaded" : `Upload ${rows.length} clip${rows.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
    </>
  );
}

function StatusCell({ row }) {
  if (row.status === "pending") return <span className="text-[11px] text-stone-500">Ready</span>;
  if (row.status === "uploading")
    return (
      <div className="text-[11px]">
        <div className="text-orange-400">Uploading {row.progress}%</div>
        <div className="h-1 bg-stone-800 mt-1 overflow-hidden">
          <div className="h-full bg-orange-500" style={{ width: `${row.progress}%` }} />
        </div>
      </div>
    );
  if (row.status === "finalizing") return <span className="text-[11px] text-orange-400">Finalizing…</span>;
  if (row.status === "done") return <span className="text-[11px] text-emerald-400 inline-flex items-center gap-1"><Check size={11} /> Done</span>;
  if (row.status === "failed")
    return (
      <span className="text-[11px] text-rose-400 inline-flex items-center gap-1" title={row.error}>
        <AlertCircle size={11} /> Failed
      </span>
    );
  return null;
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[11px] text-stone-500 mb-1">{label}{required && <span className="text-orange-500"> *</span>}</div>
      {children}
    </div>
  );
}
